// A ceiling on how many trace-reading queries one browser tab may have out at
// once.
//
// Both halves of the side-by-side reading — dating a trace's turns, and
// corroborating them against what the session's processes did — are ClickHouse
// scans, and `docs/scale-systems.md` names ClickHouse compute as the stack-wide
// binding constraint: `max_concurrent_queries` is 1000 CLUSTER-wide, with 50–100
// of it already held in steady state by callers that are not the dashboard.
//
// Nothing bounded the dashboard's share of that. One open panel can want ~22 of
// these at once — the priors read, the span read, up to eight manifest-ref
// batches, twelve detail legs — and it dispatches them without waiting, so a
// reader who opens a trace and moves straight on still pays for all of them.
// Withdrawing a read on trace switch stops the ANSWER, not the scan: the query
// runs under `wait_end_of_query`, so the server buffers the whole result and
// never notices the disconnect, and it runs to `max_execution_time` regardless.
//
// WORKLOAD MODEL, with D the server-side duration of a read (the span leg scans
// ~1.4M rows; D is bounded above by the 30s analytics `max_execution_time`), M
// the traces a reader opens per minute, and N concurrent readers. Concurrency is
// rate x duration, and the flaw before is that the rate did not depend on D:
//
//   Before, per reader: 22·M·D/60 abandoned scans still draining, plus ~22 for
//   the trace on screen. At M=12 that is ~26 when ClickHouse is healthy (D=1s)
//   and ~154 when it is not (D=30s) — the load GROWS with the slowness causing
//   it. At N=25 the degraded case is ~3,850 against a 1000-query cluster cap.
//
//   With this ceiling, per reader: at most 4 held here, plus 4·M·D/60 abandoned
//   (only the dispatched ones can be abandoned — a queued read leaves the queue
//   on the abort, never having run). At M=12: ~5 healthy, ~28 degraded. At N=25
//   the degraded case is ~700, and the healthy case is unchanged.
//
// So the ceiling is close to free in the healthy regime and roughly a 5x cut in
// the degraded one, which is the regime that matters: amplification appears only
// once ClickHouse is already slow, and that is the direction an incident moves.
//
// THE DWELL is the third term, and the only one that removes a scan rather than
// pacing it (traceReadDwell, wired at the turn clock's prior lookup — the leg
// every other read descends from). Let h be the share of opened traces a reader
// holds past the dwell; the ones below it issue nothing, so the arrival rate
// into this gate becomes h·M:
//
//   With the dwell, per reader: at most 4 held, plus 4·h·M·D/60 abandoned. At
//   h=1/3 and M=12: ~4.3 healthy, ~12 degraded. At N=25 the degraded case is
//   ~300 — against ~700 with the ceiling alone and ~3,850 with neither.
//
//   Abandoned scans CREATED, per reader per minute, at the same M=12: ~264
//   before the ceiling (every leg of every abandoned trace), ~48 with the
//   ceiling (only what got dispatched), ~16 with the dwell at h=1/3. Two of
//   every three abandoned traces now cost a cleared timer.
//
//   h is an ASSUMPTION, not a measurement — no dwell-time telemetry exists for
//   this panel. What is measured is the shape it stands on: the dated layer
//   needs the priors plus a probe before it can draw anything, so a reader who
//   leaves inside 400ms was never going to see it. The test that would close the
//   gap is counting reads issued per trace OPENED, which is one counter on this
//   module. Note the direction of the error: at h=1 the dwell changes nothing
//   and this degrades exactly to the line above it, so an optimistic h
//   overstates the saving and never the safety.
//
// The saving is also the only one here that does not scale with D. Pacing cuts
// the abandoned population in proportion to how slow ClickHouse already is; the
// dwell removes those queries whatever D turns out to be, which is why it helps
// most in the regime the pacing was written for.
//
// What remains per reader is the abandoned scans of traces genuinely read, and
// no client-side mechanism can reach those — that needs cancellation propagated
// to a ClickHouse `KILL QUERY`. What this bounds is how fast one reader can
// create them; what the dwell bounds is how many are created at all.
//
// WHY FOUR. Two structural reads and two enrichment ones, and the structural
// pair never actually contend: the span read cannot be issued until the priors
// land, because the priors are what produce the span. The manifest-ref loader
// already coalesces a burst of probes into one query per fifty ids, so a second
// slot saturates it. What is left to queue is the detail leg — and that is the
// right thing to queue, because it projects the heavy `event_data` column, it is
// per-window so it multiplies with the length of the rail, and holding one back
// costs a name appearing a beat later.
//
// QUEUE, NEVER DROP. A refused query would be a coverage cut wearing a
// performance argument: a window whose reading was skipped to save load is
// indistinguishable from one where nothing happened, which is the single claim
// this whole reading exists not to make. Bounding the rate is free of that;
// bounding the coverage is not.
//
// The dwell is not a drop by the same test: it holds the START of the reading
// and never its extent, so every window a reader who stays would have got, they
// still get. Only a trace nobody stayed on goes unread, and there is nobody to
// read it wrong.

const MAX_CONCURRENT_TRACE_READS = 4;

let active = 0;
const waiting = [];

function abortError(signal) {
  if (signal?.reason instanceof Error) return signal.reason;
  const error = new Error("trace read withdrawn");
  error.name = "AbortError";
  return error;
}

function take(signal) {
  if (signal?.aborted) return Promise.reject(abortError(signal));
  if (active < MAX_CONCURRENT_TRACE_READS) {
    active += 1;
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const waiter = { signal, resolve, reject, drop: null };
    // A queued read the reader has moved on from leaves the queue at the moment
    // of withdrawal. Left in it, it would take the next free slot to run a scan
    // whose answer is already discarded, ahead of the trace now on screen.
    waiter.drop = () => {
      const at = waiting.indexOf(waiter);
      if (at >= 0) waiting.splice(at, 1);
      reject(abortError(signal));
    };
    signal?.addEventListener("abort", waiter.drop, { once: true });
    waiting.push(waiter);
  });
}

// The slot is HANDED to the next waiter rather than released and re-taken:
// `active` counts slots that are out, and a gap between the two would let a
// caller arriving in the same tick jump the queue.
//
// A waiter whose signal has already aborted is passed over rather than handed
// the slot, and that is what makes the priority hold: one abort fires every
// listener registered on it in registration order, so the running reads of the
// trace being left release their slots BEFORE the queued reads of the same trace
// have left the queue. Without this check the first slot freed would go to a
// read of the trace the reader just closed, ahead of the one now on screen.
function give() {
  while (waiting.length) {
    const next = waiting.shift();
    next.signal?.removeEventListener("abort", next.drop);
    if (next.signal?.aborted) {
      next.reject(abortError(next.signal));
      continue;
    }
    next.resolve();
    return;
  }
  active = Math.max(0, active - 1);
}

/**
 * Run `work` under this tab's trace-read ceiling, queueing until a slot is free.
 *
 * @param {AbortSignal|null|undefined} signal  the caller's withdrawal. A read
 *   still queued when it aborts never runs and never takes a slot; a read
 *   already running gives its slot back at the instant of the abort rather than
 *   when its promise settles. That second part is what keeps a fast-switching
 *   reader from queueing behind queries nobody is waiting for — whether the wire
 *   honours the signal promptly is not this gate's to assume.
 * @param {() => Promise<any>} work  called once, only after a slot is held.
 * @returns whatever `work` resolves to; rejects with `work`'s error, or with an
 *   `AbortError` if the withdrawal came while queued.
 */
export async function withTraceReadSlot(signal, work) {
  await take(signal);
  let held = true;
  const free = () => {
    if (!held) return;
    held = false;
    signal?.removeEventListener("abort", free);
    give();
  };
  signal?.addEventListener("abort", free, { once: true });
  try {
    return await work();
  } finally {
    free();
  }
}

/** What the gate is holding. For tests and for reasoning about a wedged panel. */
export function traceReadGateState() {
  return { active, queued: waiting.length, ceiling: MAX_CONCURRENT_TRACE_READS };
}

/**
 * Every test in a run shares this module, so a suite that leaves a slot out
 * would hang the next one on a queue that never drains.
 */
export function _resetTraceReadGateForTests() {
  for (const waiter of waiting.splice(0)) {
    waiter.signal?.removeEventListener("abort", waiter.drop);
    waiter.reject(abortError(waiter.signal));
  }
  active = 0;
}
